import { useState } from 'react'
import { FileText, Search, FilePlus, Loader2 } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { Draft } from '@/data/mock'

interface DocumentListProps {
  documents: Draft[]
  activeDocId: string | null
  onDocSelect: (id: string) => void
  auditingDraftIds?: Set<string>
}

function formatCreationDate(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
}

function formatTimeAgo(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60_000)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`

  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return '1d ago'
  return `${diffDays}d ago`
}

export function DocumentList({ documents, activeDocId, onDocSelect, auditingDraftIds }: DocumentListProps) {
  const [search, setSearch] = useState('')

  const filtered = documents
    .filter((doc) => doc.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime())

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-2 pb-2 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search docs..."
            className="w-full h-8 rounded-md border border-border bg-background pl-8 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      </div>

      {/* List */}
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-0.5 px-2">
          {filtered.map((doc) => (
            <button
              key={doc.id}
              onClick={() => onDocSelect(doc.id)}
              className={`flex items-start gap-2 rounded-md px-2 py-2 text-left transition-colors w-full ${
                activeDocId === doc.id
                  ? 'bg-pharma-primary-light text-pharma-primary'
                  : 'text-foreground hover:bg-accent'
              }`}
            >
              {auditingDraftIds?.has(doc.id) ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 mt-0.5 animate-spin text-pharma-primary" strokeWidth={1.5} />
              ) : (
                <FileText className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={1.5} />
              )}
              <div className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium truncate text-slate-900">
                  {doc.title}
                </span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  {auditingDraftIds?.has(doc.id)
                    ? 'Audit running...'
                    : <>{formatCreationDate(doc.createdAt)} &middot; Opened {formatTimeAgo(doc.lastOpenedAt)}</>
                  }
                </span>
              </div>
            </button>
          ))}
          {filtered.length === 0 && documents.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 px-3 text-center">
              <FilePlus className="h-8 w-8 text-muted-foreground/40" strokeWidth={1.5} />
              <p className="text-xs text-muted-foreground font-medium">No documents yet</p>
              <p className="text-xs text-muted-foreground/70">
                Create a new document to get started.
              </p>
            </div>
          )}
          {filtered.length === 0 && documents.length > 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">No documents match your search</p>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
